import React from "react";
import {
    createStyles,
    Theme,
    withStyles,
    WithStyles
} from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";

import CloseIcon from "@material-ui/icons/Close";
interface IModalDialog {
    isActive: boolean;
    title: string;
    reverseButtons?: boolean;
    confirmButtonText?: string;
    cancelButtonText?: string;
    children?: React.ReactNode;
    confirmDisabled: boolean;
    errorText?: string;
    showError?: boolean;
    confirmAction: () => void;
    handleClose: () => void;
    preventClose?: boolean;
    disableDialogActions?: boolean;
}

const defaultProps: IModalDialog = {
    isActive: false,
    title: "Are you sure?",
    reverseButtons: false,
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
    confirmDisabled: false,
    errorText: "",
    showError: false,
    confirmAction: () => {},
    handleClose: () => {},
    preventClose: false
};

const styles = (theme: Theme) =>
    createStyles({
        closeButton: {
            position: "absolute",
            right: theme.spacing(1),
            top: theme.spacing(2),
            color: theme.palette.grey[500]
        }
    });

export interface DialogTitleProps extends WithStyles<typeof styles> {
    children: React.ReactNode;
    onClose: () => void;
    preventClose?: boolean;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, preventClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose && !preventClose ? (
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

export const useModalDialog = (initialMode = false) => {
    const [modalOpen, setModalOpen] = React.useState(initialMode);
    const toggle = () => setModalOpen(!modalOpen);
    return [modalOpen, setModalOpen, toggle] as const;
};

const ModalDialog: React.FC<IModalDialog> = ({ ...props }) => {
    const closeModal = () => {
        props.handleClose();
    };

    let hideDialogActions = false;

    if (props?.disableDialogActions) {
        hideDialogActions = props?.disableDialogActions;
    }

    return (
        <React.Fragment>
            <Dialog
                open={props.isActive}
                fullWidth={true}
                maxWidth="sm"
                onClose={closeModal}
                disableBackdropClick={props.preventClose}
                disableEscapeKeyDown={props.preventClose}
            >
                <DialogTitle
                    onClose={closeModal}
                    preventClose={props.preventClose}
                >
                    {props.title}
                </DialogTitle>
                <DialogContent dividers>
                    {props.showError ? (
                        <React.Fragment>
                            <Alert severity="error">
                                <AlertTitle>Error</AlertTitle>
                                There was an error performing the requested
                                action - {props.errorText}
                            </Alert>
                            <br />
                        </React.Fragment>
                    ) : null}
                    {props.children}
                </DialogContent>
                {!hideDialogActions && (
                    <DialogActions>
                        {props.reverseButtons ? (
                            <React.Fragment>
                                <Button
                                    autoFocus
                                    color="primary"
                                    variant="outlined"
                                    onClick={closeModal}
                                    disabled={props.preventClose}
                                >
                                    {props.cancelButtonText}
                                </Button>
                                <Button
                                    autoFocus
                                    color="primary"
                                    variant="outlined"
                                    onClick={props.confirmAction}
                                    disabled={props.confirmDisabled}
                                >
                                    {props.confirmButtonText}
                                </Button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Button
                                    autoFocus
                                    color="primary"
                                    variant="outlined"
                                    onClick={props.confirmAction}
                                    disabled={props.confirmDisabled}
                                >
                                    {props.confirmButtonText}
                                </Button>
                                <Button
                                    autoFocus
                                    color="primary"
                                    variant="outlined"
                                    onClick={closeModal}
                                    disabled={props.preventClose}
                                >
                                    {props.cancelButtonText}
                                </Button>
                            </React.Fragment>
                        )}
                    </DialogActions>
                )}
            </Dialog>
        </React.Fragment>
    );
};

ModalDialog.defaultProps = defaultProps;

export default ModalDialog;
